/**
 * Pure helpers for the admin console's Security tab (pages/Admin.jsx), over
 * GET/PUT /admin/security-settings. The server is the real validator
 * (src/shared/security/settings.js validateSettings); the bounds and enums here
 * mirror it so the form can block an obviously bad save before the round trip.
 */

export const AGGREGATIONS = [
  ['any-infected', 'Any scanner reports infected: reject'],
  ['all-clean', 'Every scanner must report clean'],
  ['majority', 'Majority verdict'],
];

export const FAIL_MODES = [
  ['closed', 'Fail closed: reject the upload'],
  ['open', 'Fail open: admit the upload unscanned'],
];

// validateSettings accepts 1000..890000 ms; the form edits whole seconds.
export const MIN_TIMEOUT_SECONDS = 1;
export const MAX_TIMEOUT_SECONDS = 890;

/** Effective settings (always fully populated by the server) -> form state. */
export function securityToForm(settings) {
  const fs = (settings && settings.fileScanning) || {};
  return {
    enabled: fs.enabled !== false,
    scanners: Array.isArray(fs.scanners) ? [...fs.scanners] : [],
    aggregation: fs.aggregation || 'any-infected',
    failMode: fs.failMode === 'open' ? 'open' : 'closed',
    timeoutSeconds: String(Math.round((fs.timeoutMs || 60000) / 1000)),
  };
}

/** Form state -> PUT body. Sends every field so the save is explicit. */
export function buildSecurityPayload(form) {
  return {
    fileScanning: {
      enabled: form.enabled === true,
      scanners: [...form.scanners],
      aggregation: form.aggregation,
      failMode: form.failMode,
      timeoutMs: Math.round(Number(form.timeoutSeconds) * 1000),
    },
  };
}

/**
 * Toggle a scanner id. Selection order is the order scanners are applied, so a
 * newly ticked scanner goes last and unticking keeps the others' order.
 */
export function toggleScanner(scanners, id, checked) {
  const without = scanners.filter(s => s !== id);
  return checked ? [...without, id] : without;
}

/** Errors that would make the server reject the save. Empty means savable. */
export function securityFormErrors(form, availableScanners = []) {
  const errors = [];
  const seconds = Number(form.timeoutSeconds);
  if (
    String(form.timeoutSeconds).trim() === '' ||
    !Number.isFinite(seconds) ||
    seconds < MIN_TIMEOUT_SECONDS ||
    seconds > MAX_TIMEOUT_SECONDS
  ) {
    errors.push(
      `Scan timeout must be between ${MIN_TIMEOUT_SECONDS} and ${MAX_TIMEOUT_SECONDS} seconds.`
    );
  }
  const known = new Set(availableScanners.map(s => s.id));
  const unknown = form.scanners.filter(id => !known.has(id));
  if (unknown.length) {
    errors.push(`Remove the scanners that are no longer available: ${unknown.join(', ')}.`);
  }
  return errors;
}

/**
 * What the chosen posture means for uploads, so the admin sees the effect of a
 * setting before saving it. Each entry is one sentence of warning text.
 */
export function securityPostureNotes(form, availableScanners = []) {
  const notes = [];
  if (!form.enabled) {
    notes.push('File scanning is off: uploads are admitted without any malware scan.');
    return notes;
  }
  if (form.scanners.length === 0) {
    notes.push(
      form.failMode === 'closed'
        ? 'No scanner is selected, so every upload that needs a scan is rejected.'
        : 'No scanner is selected and the policy fails open, so uploads are admitted unscanned.'
    );
  }
  const byId = new Map(availableScanners.map(s => [s.id, s]));
  const unconfigured = form.scanners.filter(
    id => byId.get(id) && byId.get(id).configured === false
  );
  if (unconfigured.length) {
    const names = unconfigured.map(id => byId.get(id).displayName || id).join(', ');
    notes.push(
      `Selected but not deployed in this environment: ${names}. ` +
        (form.failMode === 'closed'
          ? 'Its scans error, and failing closed rejects those uploads.'
          : 'Its scans error, and failing open admits those uploads unscanned.')
    );
  }
  if (form.failMode === 'open' && form.scanners.length > 0) {
    notes.push('Failing open admits an upload whenever a scan errors or times out.');
  }
  return notes;
}

/**
 * True when the save weakens protection compared with what is stored, so the
 * page asks for confirmation first: turning scanning off, switching to fail
 * open, or removing every scanner.
 */
export function weakensProtection(before, after) {
  if (!before) return false;
  if (before.enabled && !after.enabled) return true;
  if (after.enabled && before.failMode === 'closed' && after.failMode === 'open') return true;
  if (after.enabled && before.scanners.length > 0 && after.scanners.length === 0) return true;
  return false;
}
